import React from 'react';
import { toast } from 'react-toastify';
import { Check, Close, Warning } from '@mui/icons-material';
import { ToastStyles } from './toastStyles';

/** Pass this as `toastId` for global HTTP 500 from API layer — throttled to one visible toast per burst. */
export const GLOBAL_HTTP_500_TOAST_ID = 'global-http-500';

interface ToastContentProps {
  title: string;
  message: string;
  /** Same id = react-toastify replaces active toast (dedupe concurrent identical alerts). */
  toastId?: string;
}

/** Merge HTTP 500 alerts that fire in separate ticks (avoids dismiss→reopen flicker from multiple microtask flushes). */
let batchedHttp500: { title: string; message: string } | null = null;
let http500DebounceTimer: ReturnType<typeof setTimeout> | null = null;
const HTTP_500_DEBOUNCE_MS = 200;

const sharedErrorToastOptions = {
  position: 'top-right' as const,
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  className: 'custom-toast-error',
  icon: () => <Close style={{ color: 'white', fontSize: '20px' }} />,
  toastId: GLOBAL_HTTP_500_TOAST_ID,
};

const flushBatchedHttp500 = () => {
  http500DebounceTimer = null;
  if (!batchedHttp500) return;
  const { title, message } = batchedHttp500;
  batchedHttp500 = null;
  const content = <ToastContent title={title} message={message} />;

  if (toast.isActive(GLOBAL_HTTP_500_TOAST_ID)) {
    toast.update(GLOBAL_HTTP_500_TOAST_ID, {
      render: content,
      type: 'error',
      ...sharedErrorToastOptions,
    });
  } else {
    toast.error(content, sharedErrorToastOptions);
  }
};

const scheduleGlobalHttp500Toast = (title: string, message: string) => {
  batchedHttp500 = { title, message };
  if (http500DebounceTimer) clearTimeout(http500DebounceTimer);
  http500DebounceTimer = setTimeout(flushBatchedHttp500, HTTP_500_DEBOUNCE_MS);
};

const ToastContent = ({ title, message }: ToastContentProps) => (
  <div className="custom-toast-body">
    <div className="custom-toast-title">{title}</div>
    <div className="custom-toast-message">{message}</div>
  </div>
);

export const showSuccessToast = ({ title, message }: ToastContentProps) => {
  toast.success(<ToastContent title={title} message={message} />, {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    className: 'custom-toast-success',
    icon: () => <Check style={{ color: 'white', fontSize: '20px' }} />,
  });
};

export const showErrorToast = ({
  title,
  message,
  toastId,
}: ToastContentProps) => {
  if (toastId === GLOBAL_HTTP_500_TOAST_ID) {
    scheduleGlobalHttp500Toast(title, message);
    return;
  }

  toast.error(<ToastContent title={title} message={message} />, {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    className: 'custom-toast-error',
    icon: () => <Close style={{ color: 'white', fontSize: '20px' }} />,
    ...(toastId ? { toastId } : {}),
  });
};

export const showWarningToast = ({ title, message }: ToastContentProps) => {
  toast.warning(<ToastContent title={title} message={message} />, {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    className: 'custom-toast-warning',
    icon: () => <Warning style={{ color: 'white', fontSize: '20px' }} />,
  });
};

export { ToastStyles };
